'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  DoubleOptInTileType,
  DoubleOptInFormData,
} from '@/ui/components/tiles/DoubleOptIn/types/double-opt-in-tile-type';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import Button from '@/ui/components/shared/Button';
import { submitDoubleOptInEvent } from '@/server-functions/doubleOptIn/double-opt-in';
import { useLocalization } from '@/ui/providers/localizationProvider';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import PrivacyPolicyCheckBox from '@/ui/components/tiles/ClubRegistration/components/PrivacyPolicyCheckBox';
import { useTranslations } from 'next-intl';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { SelectInput } from '@/ui/components/shared/inputs/Dropdown/SelectInput';
import {
  CaptchaValidationErrorCodes,
  FRIENDLY_CAPTCHA_ENDPOINT,
} from '@/integrations/captcha/constants';
import { buildTrackingId } from '@/utils/tracking-helper';
import { readUserMetaCurrentCookie } from '@/utils/cookie-helper';
import { ElementStyling } from '@/integrations/crystallize/helpers/element-styling-piece-helper';
import { useClubMembershipLookup } from '@/ui/hooks/use-club-membership-lookup';

function getConsentValue(tile: DoubleOptInTileType, key: string): string {
  return tile['consent-list']?.sections[0].properties.find((prop) => prop.key === key)?.value || '';
}

type CheckboxItem = {
  key: string;
  label: string;
  required: boolean;
};

type CheckboxFieldName = `checkbox_${string}`;

function getCheckboxFieldName(key: string): CheckboxFieldName {
  return `checkbox_${key}`;
}

// Reads the `checkboxes` content chunk (key/label/required per row); the legacy
// `optional-checkboxes` PropertiesTable stays supported as an all-optional fallback.
function parseCheckboxItems(
  checkboxes: DoubleOptInTileType['checkboxes'],
  legacyCheckboxes: DoubleOptInTileType['optional-checkboxes'],
): CheckboxItem[] {
  const chunkItems = (checkboxes?.chunks || [])
    .map((chunk) => {
      const contentById: Record<string, { text?: string | null; value?: boolean | null }> = {};
      chunk.forEach((component) => {
        if (component.content) {
          contentById[component.id] = component.content;
        }
      });
      const key = contentById.key?.text || '';
      if (!key) {
        return null;
      }
      return {
        key,
        label: contentById.label?.text || key,
        required: Boolean(contentById.required?.value),
      };
    })
    .filter((item): item is CheckboxItem => item !== null);

  if (chunkItems.length > 0) {
    return chunkItems;
  }

  return (legacyCheckboxes?.sections || []).flatMap(
    (section) =>
      section.properties?.map((prop) => ({
        key: prop.key,
        label: prop.value || prop.key,
        required: false,
      })) || [],
  );
}

function buildSelectedInterests(
  formValues: DoubleOptInFormData,
  checkboxItems: Array<{ key: string; label: string }>,
): string | undefined {
  const selected = checkboxItems
    .filter((item) => formValues[getCheckboxFieldName(item.key)])
    .map((item) => item.key);
  if (formValues.dropdown) {
    selected.push(formValues.dropdown);
  }
  return selected.length > 0 ? JSON.stringify(selected) : undefined;
}

function isCaptchaError(submitError: any): boolean {
  const message = submitError?.message;
  return (
    typeof message === 'string' &&
    (message.includes(CaptchaValidationErrorCodes.SOLUTION_INVALID) ||
      message.includes(CaptchaValidationErrorCodes.SOLUTION_TIMEOUT_OR_DUPLICATE))
  );
}

// Empty during SSR; the tile only reads it on the client after user interaction.
function getCurrentPageUrl(): string {
  return globalThis.window === undefined ? '' : globalThis.location.href;
}

function getButtonTextColor(buttonBg: string): string {
  const hex = buttonBg.replace('#', '');
  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

function getLayoutClasses(
  isForCompatibilityCheckScreen: boolean,
  elementStyling?: ElementStyling,
  headlineColor?: string,
) {
  return {
    outer: isForCompatibilityCheckScreen ? 'flex w-full rounded-xl' : 'flex w-full bg-neutral-5',
    middle: isForCompatibilityCheckScreen
      ? 'mx-auto w-full overflow-x-hidden px-6 py-5'
      : 'mx-auto my-8 max-w-screen-2xl overflow-x-hidden px-4 sm:my-14 sm:px-8 lg:my-16',
    inner: isForCompatibilityCheckScreen
      ? 'mx-auto max-w-2xl space-y-4'
      : 'mx-auto my-10 max-w-2xl space-y-4 sm:my-12 sm:space-y-12',
    heading: isForCompatibilityCheckScreen
      ? 'text-[19px] font-bold leading-[1.25]'
      : 'text-3xl font-bold sm:text-4xl',
    body: isForCompatibilityCheckScreen ? 'text-[15px] leading-[1.3]' : 'text-md sm:text-2xl',
    form: isForCompatibilityCheckScreen
      ? 'mx-auto mt-4 max-w-sm space-y-3'
      : 'mx-auto mt-6 max-w-sm space-y-4',
    detailsForm: isForCompatibilityCheckScreen
      ? 'mx-auto !mt-4 max-w-sm space-y-3'
      : 'mx-auto !mt-6 max-w-sm space-y-4',
    wrapperStyle: isForCompatibilityCheckScreen
      ? { backgroundColor: elementStyling?.backgroundColor, color: elementStyling?.textColor }
      : undefined,
    headingStyle: isForCompatibilityCheckScreen ? { color: headlineColor } : undefined,
  };
}

type DoubleOptInStep = 'email' | 'details' | 'submitted';

type Translator = (key: string) => string;

interface ValidationFlags {
  isClubMember: boolean;
  isDropdownRequired: boolean;
  hasDropdownOptions: boolean;
}

function buildValidationSchema(
  t: Translator,
  checkboxItems: CheckboxItem[],
  { isClubMember, isDropdownRequired, hasDropdownOptions }: ValidationFlags,
) {
  const requiredCheckboxRules: Record<string, yup.AnySchema> = {};
  checkboxItems.forEach((item) => {
    if (item.required) {
      requiredCheckboxRules[getCheckboxFieldName(item.key)] = yup
        .boolean()
        .oneOf([true], t('This field is required'))
        .required(t('This field is required'));
    }
  });

  return yup.object().shape({
    firstName: isClubMember ? yup.string() : yup.string().required(t('First name is required')),
    lastName: isClubMember ? yup.string() : yup.string().required(t('Last name is required')),
    email: yup
      .string()
      .email(t('Please enter a valid email address'))
      .required(t('Email is required')),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('You must accept the privacy policy'))
      .required(t('You must accept the privacy policy')),
    ...(isDropdownRequired && hasDropdownOptions
      ? { dropdown: yup.string().required(t('Please select an option')) }
      : {}),
    ...requiredCheckboxRules,
  });
}

// Keeps the details step in browser history so the browser back button returns to the
// email step instead of leaving the page. Only active when the CMS enables the back button.
// Returns the handler for the in-page back button.
function useDetailsStepHistory(
  isEnabled: boolean,
  step: DoubleOptInStep,
  setStep: (step: DoubleOptInStep) => void,
) {
  useEffect(() => {
    if (!isEnabled || step !== 'details') {
      return undefined;
    }

    globalThis.history.pushState({ doubleOptInStep: 'details' }, '');
    const handlePopState = () => setStep('email');
    globalThis.addEventListener('popstate', handlePopState);

    return () => globalThis.removeEventListener('popstate', handlePopState);
  }, [isEnabled, step, setStep]);

  return () => {
    setStep('email');
    // Consumes the history entry pushed when the details step was entered.
    globalThis.history.back();
  };
}

/**
 * @param isForCompatibilityCheckScreen - When true, renders a compact variant with custom colors and
 *   smaller text sizes, used on the Installation Check results page (promotion slots).
 *   The standalone tile version (isForCompatibilityCheckScreen=false) keeps the original full-width layout.
 */
export default function DoubleOptIn(
  data: Readonly<{
    tile: DoubleOptInTileType;
    elementStyling?: ElementStyling;
    isForCompatibilityCheckScreen?: boolean;
    onSubmitted?: () => void;
    trackingPrefix?: string;
  }>,
) {
  const {
    tile,
    elementStyling,
    isForCompatibilityCheckScreen = false,
    onSubmitted,
    trackingPrefix,
  } = data;

  const t = useTranslations('DoubleOptIn.Form');
  const captchaMessages = useTranslations('captchaMessages');

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<DoubleOptInStep>('email');
  const [error, setError] = useState<string | null>(null);
  const [isClubMember, setIsClubMember] = useState(false);
  const [checkboxGroupError, setCheckboxGroupError] = useState<string | null>(null);

  const { checkMembership, isChecking } = useClubMembershipLookup();

  const localization = useLocalization();
  const { language, country } = localization || {};

  const isNukiClubSignUp = Boolean(tile['is-nuki-club-sign-up']?.value);
  const checkboxesRequireAny = Boolean(tile['checkboxes-require-any']?.value);
  const isDropdownRequired = Boolean(tile['dropdown-required']?.value);
  const showBackButton = Boolean(tile['show-back-button']?.value);

  const pageUrl = getCurrentPageUrl();

  const checkboxesChunk = tile.checkboxes;
  const legacyCheckboxes = tile['optional-checkboxes'];
  const checkboxItems = useMemo(
    () => parseCheckboxItems(checkboxesChunk, legacyCheckboxes),
    [checkboxesChunk, legacyCheckboxes],
  );

  const dropdownData = useMemo(() => {
    const items =
      tile.dropdown?.sections?.flatMap(
        (section) =>
          section.properties?.map((prop) => ({
            key: prop.key,
            label: prop.value || prop.key,
          })) || [],
      ) || [];
    const title = tile.dropdown?.sections?.[0]?.title || '';
    return { items, title };
  }, [tile.dropdown]);

  const validationSchema = buildValidationSchema(t, checkboxItems, {
    isClubMember,
    isDropdownRequired,
    hasDropdownOptions: dropdownData.items.length > 0,
  });

  const {
    register,
    control,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<DoubleOptInFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      privacyPolicy: false,
    },
  });

  const watchedFirstName = watch('firstName');
  const watchedLastName = watch('lastName');
  const watchedPrivacyPolicy = watch('privacyPolicy');
  const watchedCheckboxValues = watch(checkboxItems.map((item) => getCheckboxFieldName(item.key)));

  useEffect(() => {
    if (checkboxGroupError && watchedCheckboxValues.some(Boolean)) {
      setCheckboxGroupError(null);
    }
  }, [watchedCheckboxValues, checkboxGroupError]);

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'none',
    language: language as any,
    showAttribution: true,
  });

  useEffect(() => {
    if (captchaHook.captchaStatus.solution && !captchaHook.captchaStatus.error) {
      setError(null);
    }
  }, [captchaHook.captchaStatus]);

  const handleBack = useDetailsStepHistory(showBackButton, step, setStep);

  const isSubmitDisabled =
    (!isClubMember && (!watchedFirstName || !watchedLastName)) ||
    !watchedPrivacyPolicy ||
    captchaHook.captchaStatus.solution === null;

  const handleEmailNext = async () => {
    if (isChecking) {
      return;
    }

    const isValid = await trigger('email');
    if (!isValid) {
      return;
    }

    // Existing club members don't have to re-enter their name on the details step. This applies to
    // every double-opt-in tile, not only the ones flagged as club sign-up (AB#56559).
    try {
      const customerStatus = await checkMembership(getValues('email'));
      setIsClubMember(customerStatus.isClubMember);
    } catch {
      // A failed lookup must never block the sign-up — fall back to asking for the full name.
      setIsClubMember(false);
    }

    setStep('details');
  };

  const handleSubmit = async () => {
    const isValid = await trigger();

    const isMissingRequiredAny =
      checkboxesRequireAny &&
      checkboxItems.length > 0 &&
      !checkboxItems.some((item) => getValues(getCheckboxFieldName(item.key)));
    setCheckboxGroupError(isMissingRequiredAny ? t('Please select at least one option') : null);

    if (!isValid || isMissingRequiredAny) {
      return;
    }

    if (captchaHook.captchaStatus.solution === null) {
      setError(captchaMessages('Please complete the CAPTCHA before submitting'));
      return;
    }

    const formValues = getValues();
    const { firstName, lastName, email } = formValues;
    const interests = buildSelectedInterests(formValues, checkboxItems);
    const source = tile.source?.text || pageUrl.split('/').findLast(Boolean) || '';
    const umc = readUserMetaCurrentCookie();

    try {
      setLoading(true);
      await submitDoubleOptInEvent({
        email,
        action: getConsentValue(tile, 'action'),
        category: getConsentValue(tile, 'category'),
        validUntil: getConsentValue(tile, 'valid_until'),
        nukiProductId: tile.sku?.text || '',
        language,
        pageUrl,
        source,
        firstName: isClubMember ? undefined : firstName,
        lastName: isClubMember ? undefined : lastName,
        interests,
        frcCaptchaSolution: captchaHook.captchaStatus.solution,
        isNukiClubSignUp,
        country,
        tracking: {
          utmSource: umc?.source,
          utmMedium: umc?.medium,
          utmCampaign: umc?.campaignName,
          utmContent: umc?.content,
          nag: umc?.nag,
        },
      });
      setLoading(false);
      setStep('submitted');
      if (onSubmitted) {
        onSubmitted();
      }
    } catch (submitError: any) {
      setLoading(false);
      if (isCaptchaError(submitError)) {
        setError(captchaMessages('Captcha validation failed Please try again'));
        captchaHook.resetWidget();
        return;
      }
      setError(t('An error occurred while processing your request Please try again later'));
    }
  };

  const buttonBg = elementStyling?.buttonColor || '#FECB3F';
  const buttonTextColor = getButtonTextColor(buttonBg);

  const emailButtonTrackingId = buildTrackingId(
    trackingPrefix,
    'email-step',
    tile.buttontext?.text,
  );
  const detailsButtonTrackingId = buildTrackingId(
    trackingPrefix,
    'details-step',
    tile.buttontext?.text,
  );
  const backButtonTrackingId = buildTrackingId(trackingPrefix, 'details-step', 'back');

  const layout = getLayoutClasses(
    isForCompatibilityCheckScreen,
    elementStyling,
    tile['headline-color']?.options?.[0]?.value ?? undefined,
  );

  return (
    <div className={layout.outer} style={layout.wrapperStyle}>
      <div className={layout.middle}>
        <div className={layout.inner}>
          {step === 'submitted' && (
            <div className="space-y-2 text-center sm:space-y-3">
              <h1 className={layout.heading} style={layout.headingStyle}>
                {formatPunctuation(tile.thankyouheadline?.text)}
              </h1>
              <p className={layout.body}>{tile.thankyoutext?.plainText}</p>
              {tile['thank-you-cta'] && (
                <ButtonLink
                  ctaComponent={tile['thank-you-cta']}
                  className="mx-auto w-full max-w-sm"
                  trackingPrefix={trackingPrefix}
                />
              )}
            </div>
          )}
          {step === 'email' && (
            <>
              <div className="space-y-2 text-center sm:space-y-3">
                <h1 className={layout.heading} style={layout.headingStyle}>
                  {formatPunctuation(tile.headline?.text)}
                </h1>
                <p className={layout.body}>{tile.text?.plainText}</p>
              </div>
              <form className={layout.form}>
                <TextInput
                  id="email"
                  srLabel={tile.inputplaceholder?.text}
                  register={register}
                  errors={errors.email}
                  placeholder={tile.inputplaceholder?.text}
                  className="w-full"
                  inputWrapperClassName={
                    isForCompatibilityCheckScreen
                      ? 'rounded-[10px] px-3 py-[10px] text-[14px] text-neutral-100'
                      : undefined
                  }
                  makeRounded={!isForCompatibilityCheckScreen}
                  type="email"
                />
                <Button
                  type="button"
                  className={
                    isForCompatibilityCheckScreen
                      ? 'h-[44px] w-full rounded-[12px] border-0 py-0 text-[14px] shadow-[inset_0_0_0_1px_#000] active:opacity-70'
                      : 'mx-auto w-full max-w-sm'
                  }
                  variant={isForCompatibilityCheckScreen ? 'whiteBlackBorder' : 'primary'}
                  style={
                    isForCompatibilityCheckScreen
                      ? { backgroundColor: buttonBg, color: buttonTextColor }
                      : undefined
                  }
                  loading={isChecking}
                  onClick={handleEmailNext}
                  data-tile-element-id={emailButtonTrackingId}
                >
                  {tile.buttontext?.text}
                </Button>
                {error && <p className="mt-1 text-center text-sm text-red">{error}</p>}
              </form>
              {isForCompatibilityCheckScreen && (
                <p className="text-center text-[12px] leading-[1.2] opacity-70">
                  {t('disclaimer')}
                </p>
              )}
            </>
          )}
          {step === 'details' && (
            <>
              <div className="space-y-2 text-center sm:space-y-3">
                <h1 className={layout.heading} style={layout.headingStyle}>
                  {formatPunctuation(tile.headline?.text)}
                </h1>
                {checkboxItems.length > 0 && (
                  <>
                    {Boolean(tile['checkboxes-label']?.plainText?.length) && (
                      <p className={layout.body}>{tile['checkboxes-label']?.plainText}</p>
                    )}
                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 pt-1">
                      {checkboxItems.map((item) => {
                        const fieldName = getCheckboxFieldName(item.key);
                        const fieldError = errors[fieldName];

                        return (
                          <div key={item.key} className="flex flex-col items-start">
                            <div className="flex items-center space-x-2">
                              <input
                                id={fieldName}
                                type="checkbox"
                                {...register(fieldName)}
                                className="h-4 w-4 cursor-pointer"
                                aria-describedby={fieldError ? `${fieldName}-error` : undefined}
                              />
                              <label
                                htmlFor={fieldName}
                                className={
                                  isForCompatibilityCheckScreen
                                    ? 'cursor-pointer text-sm'
                                    : 'cursor-pointer text-sm text-neutral-50'
                                }
                              >
                                {item.label}
                              </label>
                            </div>
                            {fieldError && (
                              <span id={`${fieldName}-error`} className="text-sm text-red">
                                {fieldError.message}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {checkboxGroupError && (
                      <p className="text-sm text-red" role="alert">
                        {checkboxGroupError}
                      </p>
                    )}
                  </>
                )}
              </div>
              <form className={layout.detailsForm}>
                {dropdownData.items.length > 0 && (
                  <SelectInput
                    name="dropdown"
                    control={control}
                    options={dropdownData.items.map((item) => ({
                      value: item.key,
                      label: item.label,
                    }))}
                    placeholder={dropdownData.title || t('Please select')}
                    optionsClass="rounded-3xl text-neutral-100"
                    buttonClass={
                      isForCompatibilityCheckScreen
                        ? 'rounded-[10px] text-[14px] text-neutral-100'
                        : 'rounded-full px-4 text-sm text-neutral-100'
                    }
                  />
                )}
                {!isClubMember && (
                  <>
                    <TextInput
                      id="firstName"
                      srLabel={t('First Name')}
                      register={register}
                      errors={errors.firstName}
                      placeholder={t('First Name')}
                      className="w-full"
                      inputWrapperClassName={
                        isForCompatibilityCheckScreen
                          ? 'rounded-[10px] px-3 py-[10px] text-[14px] text-neutral-100'
                          : undefined
                      }
                      makeRounded={!isForCompatibilityCheckScreen}
                    />
                    <TextInput
                      id="lastName"
                      srLabel={t('Last Name')}
                      register={register}
                      errors={errors.lastName}
                      placeholder={t('Last Name')}
                      className="w-full"
                      inputWrapperClassName={
                        isForCompatibilityCheckScreen
                          ? 'rounded-[10px] px-3 py-[10px] text-[14px] text-neutral-100'
                          : undefined
                      }
                      makeRounded={!isForCompatibilityCheckScreen}
                    />
                  </>
                )}
                <PrivacyPolicyCheckBox
                  register={register}
                  errors={errors.privacyPolicy}
                  labelClassName={
                    isForCompatibilityCheckScreen ? 'cursor-pointer text-sm' : undefined
                  }
                />

                {isForCompatibilityCheckScreen ? (
                  <div className="rounded-[10px] bg-white px-3 py-3 text-neutral-100">
                    {captchaHook.CaptchaWidget(
                      { className: 'relative pb-4' },
                      { button: { border: '1px solid var(--neutral-50)' } },
                    )}
                  </div>
                ) : (
                  captchaHook.CaptchaWidget(
                    { className: 'my-5 relative pb-4' },
                    { button: { border: '1px solid var(--neutral-50)' } },
                  )
                )}

                <Button
                  disabled={isSubmitDisabled}
                  type="button"
                  className={
                    isForCompatibilityCheckScreen
                      ? 'h-[44px] w-full rounded-[12px] border-0 py-0 text-[14px] shadow-[inset_0_0_0_1px_#000] active:opacity-70'
                      : 'mx-auto w-full max-w-sm'
                  }
                  variant={isForCompatibilityCheckScreen ? 'whiteBlackBorder' : 'primary'}
                  style={
                    isForCompatibilityCheckScreen && !isSubmitDisabled
                      ? { backgroundColor: buttonBg, color: buttonTextColor }
                      : undefined
                  }
                  loading={loading}
                  onClick={handleSubmit}
                  data-tile-element-id={detailsButtonTrackingId}
                >
                  {tile.buttontext?.text}
                </Button>

                {showBackButton && (
                  <Button
                    type="button"
                    variant="secondary"
                    className={
                      isForCompatibilityCheckScreen
                        ? 'h-[44px] w-full rounded-[12px] border-0 py-0 text-[14px] active:opacity-70'
                        : 'mx-auto w-full max-w-sm'
                    }
                    onClick={handleBack}
                    data-tile-element-id={backButtonTrackingId}
                  >
                    {t('Back')}
                  </Button>
                )}

                {error && <p className="mt-1 text-center text-sm text-red">{error}</p>}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
